import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useState , useEffect } from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
// @mui
import LoadingButton from '@mui/lab/LoadingButton';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
// routes
import { RouterLink } from 'src/routes/components';
import { useRouter, useSearchParams } from 'src/routes/hooks';
// components
import { Box } from '@mui/material';
import { toast } from 'react-toastify';
import FormProvider, { RHFTextField, RHFUploadAvatar } from 'src/components/hook-form';
import Iconify from 'src/components/iconify';
import { VERIFY_PASSWORD } from 'src/config-global';
import { paths } from 'src/routes/paths';
import { useUploadImageMutation } from 'src/store/Reducer/file';
import { useRegisterMutation } from "../../../store/Reducer/auth";
import { selectDeviceId } from 'src/store/slices/userSlice';
import { useSelector } from 'react-redux';

export default function JwtRegisterView() {

   const [userTimeZone, setUserTimeZone] = useState('');

    const [registerData] = useRegisterMutation();
    const [uploadImage] = useUploadImageMutation();
    const router = useRouter();
    const searchParams = useSearchParams();
    const returnTo = searchParams.get('returnTo');
    const password = useBoolean();
    const confirmPassword = useBoolean();
    
    const deviceId = useSelector(selectDeviceId);

   useEffect(() => {
      const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      setUserTimeZone(userTimeZone);
    }, []);

  const RegisterSchema = Yup.object().shape({
    avatar: Yup.mixed().nullable(),
    fullName: Yup.string().required('First name required').max(40, 'Name cannot exceed 40 characters'),
    email: Yup.string().required('Email is required').email('Email must be a valid email address'),
    password: Yup.string()
      .min(8, 'Password must be at least 8 characters long')
      .required('Password is required'),
    confirmPassword: Yup.string()
      .required('Confirm Password is required')
      .oneOf([Yup.ref('password'), null], 'Passwords must match'),
  });
  const defaultValues = {
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
  };
  const methods = useForm({
    resolver: yupResolver(RegisterSchema),
    defaultValues,
  });
  const {
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

 const onSubmit = handleSubmit(async (data) => {
  try {
    let imageUrl = '';
    if (data?.avatar) {
      const formData = new FormData();
      formData.append('files[]', data.avatar);
      formData.append('UPLOAD_IDENTIFIER', 'my_upload_1');
      const uploadResult = await uploadImage(formData).unwrap();
      if (!uploadResult) {
        toast.error('Image upload failed');
        return;
      }
      imageUrl = uploadResult.data.uploaded[0].file;
    }
    const newData = {
      image: imageUrl,
      name: data.fullName,
      email: data.email,
      password: data.password,
      device_id: deviceId || 'abcdefghijklmnopqrstuvwxyz',
      device_type: 'web',
      timezone:userTimeZone,
    };
    await registerData(newData).unwrap();
    toast.success('Registration successful!');
    router.push(returnTo || `${VERIFY_PASSWORD}?email=${encodeURIComponent(data.email)}`);
  } catch (error) {
    console.error(error);
    toast.error(error?.data?.message || 'Failed to Register');
  }
});

  const handleDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      methods.setValue(
        'avatar',
        Object.assign(file, {
          preview: URL.createObjectURL(file),
        })
      );
    }
  }, [methods]);

  const renderHead = (
    <Stack spacing={2} sx={{ mb: 2, position: 'relative' }}>
      <Typography variant="h4">Get started absolutely free</Typography>

      <Stack direction="row" spacing={0.5}>
        <Typography variant="body2"> Already have an account? </Typography>

        <Link href={paths.auth.jwt.login} component={RouterLink} variant="subtitle2">
          Sign in
        </Link>
      </Stack>
    </Stack>
  );

  const renderTerms = (
    <Typography
      component="div"
      sx={{
        color: 'text.secondary',
        mt: 2.5,
        typography: 'caption',
        textAlign: 'center',
      }}
    >
      {'By signing up, I agree to '}
      <Link underline="always" color="text.primary">
        Terms of Service
      </Link>
      {' and '}
      <Link underline="always" color="text.primary">
        Privacy Policy
      </Link>
      .
    </Typography>
  );

  const renderForm = (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <Stack spacing={2.5}>
        <RHFUploadAvatar name="avatar" label="Profile picture" maxSize={3145728} onDrop={handleDrop}/>

          <RHFTextField name="fullName" label="Full name" />
        <RHFTextField name="email" label="Email address" />
        <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
          <RHFTextField
            name="password"
            label="Password"
            type={password.value ? 'text' : 'password'}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={password.onToggle} edge="end">
                    <Iconify icon={password.value ? 'solar:eye-bold' : 'solar:eye-closed-bold'} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <RHFTextField
            name="confirmPassword"
            label="Confirm Password"
            type={confirmPassword.value ? 'text' : 'password'}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={confirmPassword.onToggle} edge="end">
                    <Iconify icon={confirmPassword.value ? 'solar:eye-bold' : 'solar:eye-closed-bold'} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* <RHFCheckbox name="term" label="I accept terms & conditions." /> */}

        <LoadingButton
          fullWidth
          color="primary"
          size="large"
          type="submit"
          variant="contained"
          loading={isSubmitting}
        >
          Create account
        </LoadingButton>
      </Stack>
    </FormProvider>
  );

  return (
    <Box  >
      {renderHead}

      {renderForm}

      {/* {renderTerms} */}
    </Box>
  );
}
